import {useCallback, useRef, useState} from 'react';
import * as Yup from 'yup';
import {Formik} from 'formik';
import {connect} from 'react-redux';
import {NavLink} from 'react-router-dom';
import Logo from '../../../assets/images/logo.png';
import {CircularProgress} from '@material-ui/core';
import {Actions} from '../../../internals/app/Actions';
import AuthRepository from '../../../internals/repository/AuthRepository';
import MixpanelService, {MP_EVENTS} from '../../../internals/service/MixpanelService';
import Recaptcha, {
  RecaptchaHandle,
  recaptchaEnabled,
} from '../../../components/Recaptcha';
import {
  AuthLayout,
  AuthHeading,
  AuthFooterNote,
  AirField,
  AirPassword,
  PrimaryButton,
  GoogleButton,
  BRAND_DARK,
} from '../components/AuthUI';

/**
 * Sign in — same Air treatment as sign up, sharing the layout and form
 * primitives in ../components/AuthUI.
 *
 * No eyebrow pill here: sign up's reads "NEW · Create your store", which
 * advertises the opposite action to the one someone on this page is taking.
 *
 * Wiring is unchanged: Formik + Yup, the redux `login` action, and Cognito
 * federated (Google) sign-in with its Mixpanel event.
 */
const SignInView = (props: any) => {
  const {login, loadingAction} = props;
  const {action, loading} = loadingAction;
  const [showPassword, setShowPassword] = useState(false);

  // null until the checkbox is solved. The pool's pre-authentication trigger
  // is what actually verifies it; gating the button here is only so the user
  // finds out before submitting rather than after a rejected sign-in.
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaHandle>(null);
  const onRecaptchaChange = useCallback(
    (token: string | null) => setRecaptchaToken(token),
    [],
  );

  const onClickSubmit = (values: any) => {
    login(values.email, values.password, recaptchaToken);
    // A v2 token is single-use. Clear the tick so a failed attempt cannot be
    // retried with a token Google will now reject.
    recaptchaRef.current?.reset();
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Must be 8 characters or more')
      .required('Password is required'),
  });

  const federatedLogin = async () => {
    const data = await AuthRepository.federatedSignIn();
    MixpanelService.track(MP_EVENTS.SIGN_UP_GOOGLE, data);
  };

  const loadingLogin = loading && action.type === 'LOGIN';

  return (
    <AuthLayout
      logo={Logo}
      image="/images/cafe.jpg"
      imageAlt="A Netlise merchant serving a customer"
    >
      <AuthHeading>Login</AuthHeading>

      <Formik
        onSubmit={(values) => onClickSubmit(values)}
        enableReinitialize={true}
        initialValues={{email: '', password: ''}}
        validationSchema={validationSchema}
      >
        {({values, errors, touched, handleSubmit, handleChange}) => (
          <form
            onSubmit={handleSubmit}
            style={{display: 'flex', flexDirection: 'column', gap: 20}}
          >
            <AirField
              id="email"
              type="email"
              label="Your email"
              placeholder="name@company.com"
              value={values.email}
              error={errors.email}
              touched={touched.email}
              onChange={handleChange('email')}
            />

            <AirPassword
              id="password"
              label="Password"
              value={values.password}
              error={errors.password}
              touched={touched.password}
              onChange={handleChange('password')}
              shown={showPassword}
              onToggle={() => setShowPassword((v) => !v)}
            />

            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: -6}}>
              <NavLink
                to="/forget-password"
                style={{fontSize: 14, fontWeight: 500, color: BRAND_DARK}}
              >
                Forget password
              </NavLink>
            </div>

            <Recaptcha ref={recaptchaRef} onChange={onRecaptchaChange} />

            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              <PrimaryButton busy={loadingLogin || (recaptchaEnabled() && !recaptchaToken)}>
                {loadingLogin ? (
                  <CircularProgress size={18} style={{color: '#fff'}} />
                ) : (
                  'Login'
                )}
              </PrimaryButton>
              <GoogleButton busy={loadingLogin} onClick={federatedLogin}>
                Login with Google
              </GoogleButton>
            </div>

            <AuthFooterNote>
              Not registered?{' '}
              <NavLink to="/signup" style={{color: BRAND_DARK, fontWeight: 600}}>
                Create account
              </NavLink>
            </AuthFooterNote>
          </form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default connect(
  (state: any) => ({
    loadingAction: state.common.get('loadingAction'),
  }),
  {
    login: Actions.login.login,
  }
)(SignInView);
